import { useState } from "react";
import { Outlet, NavLink, useParams, useLocation } from "react-router-dom";
import { UserMenu } from "@/components/UserMenu";
import { OrgSwitcher } from "@/components/OrgSwitcher";
import {
  Home,
  Clock,
  Settings,
  FileText,
  FolderKanban,
  Users,
  Users2,
  ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/utils";

const navItems = [
  { path: "dashboard", label: "Home", icon: Home },
  { path: "time", label: "Time", icon: Clock },
];

const settingsItems = [
  { path: "settings/members", label: "Members", icon: Users },
  { path: "settings/teams", label: "Teams", icon: Users2 },
  { path: "settings/projects", label: "Projects", icon: FolderKanban },
  { path: "settings/forms", label: "Forms", icon: FileText },
];

export function AppLayout() {
  const { orgId, slug } = useParams<{ orgId: string; slug: string }>();
  const location = useLocation();
  const isSettingsActive = location.pathname.includes("/settings");
  const [settingsOpen, setSettingsOpen] = useState(isSettingsActive);

  const base = `/${orgId}/${slug}`;

  return (
    <div className="flex h-screen">
      {/* Sidebar */}
      <aside className="hidden w-64 flex-col border-r bg-sidebar md:flex">
        <div className="p-4">
          <h2 className="mb-4 text-lg font-semibold tracking-tight">
            Portal
          </h2>
          <OrgSwitcher />
        </div>
        <nav className="flex-1 space-y-1 px-3 py-2">
          {navItems.map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={`${base}/${path}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                )
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}

          {/* Settings section */}
          <button
            onClick={() => setSettingsOpen(!settingsOpen)}
            className={cn(
              "flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-colors",
              isSettingsActive
                ? "text-sidebar-accent-foreground"
                : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
            )}
          >
            <span className="flex items-center gap-3">
              <Settings className="h-4 w-4" />
              Settings
            </span>
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 transition-transform",
                settingsOpen && "rotate-180"
              )}
            />
          </button>
          {settingsOpen && (
            <div className="ml-4 space-y-1">
              {settingsItems.map(({ path, label, icon: Icon }) => (
                <NavLink
                  key={path}
                  to={`${base}/${path}`}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                        : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                    )
                  }
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </NavLink>
              ))}
            </div>
          )}
        </nav>
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col">
        {/* Top bar */}
        <header className="flex h-14 items-center justify-between border-b px-4 md:justify-end">
          <div className="md:hidden">
            <h2 className="text-lg font-semibold">Portal</h2>
          </div>
          <UserMenu />
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-6">
          <div className="mx-auto w-full max-w-5xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
